// Guards the org `team` object against a known corruption: when a stringified
// object ("[object Object]") gets spread into `team`, the map ends up polluted
// with character-index keys ({ '0':'[', '1':'o', … '14':']' }) alongside the
// real role arrays. Every blind `{ ...team }` re-persists that junk. Run the
// value through this before spreading or writing so only valid role keys with
// array values survive.

/** Canonical org team role keys (see fieldConfigSections TEAM_ROLES). */
export const TEAM_ROLE_KEYS = ['AM', 'PM', 'DMS', 'SEO', 'MIA', 'BDE', 'QA', 'other'] as const

export type TeamRoleKey = (typeof TEAM_ROLE_KEYS)[number]
export type TeamObject = Partial<Record<TeamRoleKey, unknown[]>>

/**
 * Return a clean team object containing only valid role keys, each mapped to an
 * array. Numeric/character-index keys and any other junk are dropped. A
 * non-object input (e.g. the string "[object Object]") yields an empty object.
 */
export const sanitizeTeam = (team: unknown): TeamObject => {
  // Only a plain object can hold role arrays. Strings (e.g. "[object Object]"),
  // arrays, null/undefined → clean slate.
  if (!team || typeof team !== 'object' || Array.isArray(team)) {
    return {}
  }
  const source = team as Record<string, unknown>
  const clean: TeamObject = {}
  for (const key of TEAM_ROLE_KEYS) {
    const value = source[key]
    if (Array.isArray(value)) {
      clean[key] = value
    }
  }
  return clean
}

/**
 * True when a team value carries the corruption fingerprint — any key that is
 * not a known role (character-index keys, stray strings, etc.).
 */
export const isTeamCorrupted = (team: unknown): boolean => {
  if (!team || typeof team !== 'object' || Array.isArray(team)) {
    return typeof team === 'string' // a bare string in the team slot is itself corruption
  }
  const roleSet = new Set<string>(TEAM_ROLE_KEYS)
  return Object.keys(team as Record<string, unknown>).some(key => !roleSet.has(key))
}
